/**
 * QaPanel.tsx — Zone 3: Q&A Workspace (inline, not a modal)
 *
 * Shows questions for the currently-selected CP.
 * Answers live-update the helix orb color, legend score, and composite.
 *
 * Features:
 *   - CP header: number + gate name + current score
 *   - All questions for this CP in a stacked list (scannable)
 *   - 5-point graded scale + skip (locked visual language from calculator v2.2)
 *   - Live update on every answer change (calls onAnswer immediately)
 *   - Auto-save on blur (textarea notes)
 *   - Save button per question
 *   - "Next CP" button advances to the next gate
 *   - Coaching subsection: Purpose / In Conversation / On the Fence / Overall Impact
 *
 * Scale:
 *   5 — Strong yes / confirmed / fully in place   → 4 pts  (#16A34A)
 *   4 — Mostly yes / likely / mostly in place     → 3 pts  (#16A34A)
 *   3 — Partial / mixed signals / unclear         → 2 pts  (#EAB308)
 *   2 — Mostly no / weak / mostly missing         → 1 pt   (#EA580C)
 *   1 — Hard no / blocked / absent                → 0 pts  (#DC2626)
 *   Skip — unknown for now                        → null (excluded from score)
 *
 * Score math:
 *   per_cp_score = round( (sum_points_answered / (4 × count_answered)) × 100 )
 *   composite    = round( (sum_all_points / (4 × count_all_answered)) × 100 )
 *   Skip excluded from numerator AND denominator.
 *   Zero answered → render "—".
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import {
  Save,
  ArrowRight,
  BookOpen,
  MessageSquare,
  Volume2,
  VolumeX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ─── Chime / haptic helpers ──────────────────────────────────────────────────
const CHIME_KEY = "aip.qa.chime";
function chimeEnabled(): boolean {
  try { return localStorage.getItem(CHIME_KEY) === "true"; } catch { return false; }
}
function setChimeEnabled(v: boolean): void {
  try { localStorage.setItem(CHIME_KEY, v ? "true" : "false"); } catch {}
}
function playChime(): void {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.09);
    setTimeout(() => { try { ctx.close(); } catch {} }, 200);
  } catch {}
  try { (navigator as any).vibrate?.([20]); } catch {}
}
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";
import {
  detectCrossCpMentions,
  type CrossCpMatch,
} from "@/lib/cross-cp-prompts";
import { CP_COACHING } from "@/lib/cp-coaching";
import { dimensionIdForCp } from "@/lib/cp-dimensions";
import { ALL_QUESTIONS, getActiveQuestions } from "@shared/questions";
import { getHealthColor } from "@shared/schema";
import { useQuestionCoaching } from "@/lib/question-coaching";
import type { QuestionCoaching } from "@/lib/question-coaching";
import type { AnswerStore } from "./OperationalHelix";
import Q93SubQuestions from "./Q93SubQuestions";
import { Q93_SUBQ_KEYS } from "@/lib/q93_soft_stop_logic";
import IKnowBadge from "@/components/evidence/IKnowBadge";
import EvidenceSlot from "@/components/evidence/EvidenceSlot";

const Q93_SUBQ_KEY_SET = new Set<string>(Q93_SUBQ_KEYS);

// ─── Locked 5-point graded scale (identical to calculator v2.2) ──────────────

interface GradedOption {
  /** Display number 5..1 */
  display: number;
  /** Raw point value 4..0 stored as string in selectedOption */
  value: string;
  /** Points awarded */
  points: number;
  /** Anchor label */
  anchor: string;
  /** Full label */
  label: string;
  /** 4px left-border color */
  borderColor: string;
}

const GRADED_OPTIONS: GradedOption[] = [
  { display: 5, value: "4", points: 4, anchor: "Strong",  label: "Strong yes — confirmed / fully in place",  borderColor: "#16A34A" },
  { display: 4, value: "3", points: 3, anchor: "Mostly",  label: "Mostly yes — likely / mostly in place",    borderColor: "#16A34A" },
  { display: 3, value: "2", points: 2, anchor: "Partial", label: "Partial — mixed signals / unclear",        borderColor: "#EAB308" },
  { display: 2, value: "1", points: 1, anchor: "Weak",    label: "Weak — mostly missing / mostly no",        borderColor: "#EA580C" },
  { display: 1, value: "0", points: 0, anchor: "Absent",  label: "Absent — hard no / blocked",               borderColor: "#DC2626" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Per-CP score → canon palette per CANON.md §5 (@shared/schema).
function scoreColor(pct: number): string {
  return getHealthColor(pct);
}
function scoreBg(pct: number): string {
  return `${getHealthColor(pct)}1F`; // ~12% alpha tint
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  selectedCp: number | null;
  answers: AnswerStore;
  industry?: string | null;
  onAnswer: (questionKey: string, selectedOption: string | null, notes: string) => void;
  onNextCp: () => void;
  cpScores: Array<{ cp: number; pct: number }>;
  /** v2: closes the right rail and returns helix to home (Esc / X / backdrop). */
  onClose?: () => void;
  // TICKET-4: evidence support
  /** Numeric account ID for evidence API calls */
  accountId?: string;
  /** Supabase auth token for evidence API calls */
  authToken?: string;
  /** Called when evidence is saved for a question */
  onEvidenceSaved?: (questionKey: string, evidenceType: string | null, isAssumption: boolean) => void;
  /** When true, disables all answer-entry controls and shows a read-only badge.
   *  Orb and legend navigation remains interactive. Defaults to false. */
  readOnly?: boolean;
}

type PanelTab = "unanswered" | "answered" | "coaching";

// ─── Component ───────────────────────────────────────────────────────────────

export default function QaPanel({
  selectedCp,
  answers,
  industry,
  onAnswer,
  onNextCp,
  cpScores,
  onClose,
  accountId,
  authToken,
  onEvidenceSaved,
  readOnly = false,
}: Props) {
  const [tab, setTab] = useState<PanelTab>("unanswered");
  const { coachingMap } = useQuestionCoaching();
  const [chimeOn, setChimeOn] = useState<boolean>(() => chimeEnabled());
  const [openSeq, setOpenSeq] = useState(0);
  const prevCpRef = useRef<number | null>(null);
  const questionSeqRef = useRef(0);

  // Detect closed → open transition
  useEffect(() => {
    const prev = prevCpRef.current;
    if (prev === null && selectedCp !== null) {
      setOpenSeq((n) => n + 1);
      if (chimeOn) playChime();
    }
    prevCpRef.current = selectedCp;
  }, [selectedCp, chimeOn]);

  // Esc closes the right rail
  useEffect(() => {
    if (selectedCp === null || !onClose) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose && onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedCp, onClose]);

  const [drafts, setDrafts] = useState<
    Record<string, { selectedOption: string | null; notes: string }>
  >({});

  // M-009: per-question cross-CP match state
  const [crossCpMatches, setCrossCpMatches] = useState<Record<string, CrossCpMatch[]>>({});
  const [crossCpToast, setCrossCpToast] = useState<string | null>(null);

  function handleNotesCrossCpCheck(qKey: string, notes: string) {
    const cpNum = selectedCp ?? 0;
    if (!notes.trim()) {
      setCrossCpMatches((prev) => ({ ...prev, [qKey]: [] }));
      return;
    }
    const matches = detectCrossCpMentions(notes, cpNum);
    setCrossCpMatches((prev) => ({ ...prev, [qKey]: matches }));
  }

  function dismissCrossCpMatch(qKey: string, cp: number) {
    setCrossCpMatches((prev) => ({
      ...prev,
      [qKey]: (prev[qKey] ?? []).filter((m) => m.cp !== cp),
    }));
  }

  function handleCrossCpLog(qKey: string, match: CrossCpMatch) {
    setCrossCpToast(
      `We will add that link in v1.1 — for now, navigate to CP${match.cp} (${match.cpLabel}) manually.`,
    );
    setTimeout(() => setCrossCpToast(null), 4000);
    dismissCrossCpMatch(qKey, match.cp);
  }

  const cp = useMemo(
    () =>
      selectedCp !== null
        ? HELIX_CHECKPOINTS.find((c) => c.number === selectedCp) ?? null
        : null,
    [selectedCp]
  );

  const questions = useMemo(() => {
    if (selectedCp === null) return [];
    // W43.11: Use getActiveQuestions to apply industryTrigger filter.
    // Prevents industry-segmented "Mfg —" / "FM —" questions from appearing
    // for accounts in other industries (e.g. Automotive).
    return getActiveQuestions({ industry: industry ?? null, customerPath: null }).filter(
      (q) => q.cp === selectedCp && !Q93_SUBQ_KEY_SET.has(q.key),
    );
  }, [selectedCp, industry]);

  // Q9.3.A-D paper-process sub-questions render in a dedicated grouped
  // card (Q93SubQuestions), not in the graded-scale loop above.
  const q93SubQuestions = useMemo(() => {
    if (selectedCp !== 9) return [];
    return ALL_QUESTIONS.filter((q) => Q93_SUBQ_KEY_SET.has(q.key));
  }, [selectedCp]);

  const q93SelectedByKey = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const k of Q93_SUBQ_KEYS) {
      out[k] = answers[k]?.selectedOption ?? null;
    }
    return out;
  }, [answers]);

  // Full CP question key list (graded + Q9.3 sub-Qs) for counts.
  // W43.11: Apply industry filter so counts exclude industry-segmented extras.
  const allCpQuestionKeys = useMemo(() => {
    if (selectedCp === null) return [] as string[];
    return getActiveQuestions({ industry: industry ?? null, customerPath: null })
      .filter((q) => q.cp === selectedCp)
      .map((q) => q.key);
  }, [selectedCp, industry]);

  const cpScore = useMemo(
    () => cpScores.find((s) => s.cp === selectedCp) ?? { cp: selectedCp ?? 0, pct: -1 },
    [cpScores, selectedCp]
  );

  // Seed drafts when CP changes
  useEffect(() => {
    if (selectedCp === null) return;
    const init: typeof drafts = {};
    ALL_QUESTIONS.filter((q) => q.cp === selectedCp).forEach((q) => {
      const ex = answers[q.key];
      init[q.key] = {
        selectedOption: ex?.selectedOption ?? null,
        notes: ex?.notes ?? "",
      };
    });
    setDrafts(init);
    setTab("unanswered");
    questionSeqRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCp]);

  // Sync external answer updates into drafts
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      questions.forEach((q) => {
        const ex = answers[q.key];
        if (ex && next[q.key]) {
          if (ex.selectedOption !== next[q.key].selectedOption) {
            next[q.key] = { ...next[q.key], selectedOption: ex.selectedOption };
          }
        }
      });
      return next;
    });
  }, [answers, questions]);

  function handleOptionSelect(qKey: string, value: string) {
    const current = drafts[qKey]?.selectedOption ?? null;
    const next = current === value ? null : value;
    setDrafts((prev) => ({
      ...prev,
      [qKey]: { ...prev[qKey], selectedOption: next },
    }));
    onAnswer(qKey, next, drafts[qKey]?.notes ?? "");
    questionSeqRef.current += 1;
    window.dispatchEvent(
      new CustomEvent("aip:question-active", { detail: { seq: questionSeqRef.current } })
    );
  }

  function handleSkip(qKey: string) {
    setDrafts((prev) => ({
      ...prev,
      [qKey]: { ...prev[qKey], selectedOption: null },
    }));
    onAnswer(qKey, null, drafts[qKey]?.notes ?? "");
  }

  function handleNotesChange(qKey: string, notes: string) {
    setDrafts((prev) => ({ ...prev, [qKey]: { ...prev[qKey], notes } }));
  }

  function handleNotesSave(qKey: string) {
    const d = drafts[qKey];
    if (d) onAnswer(qKey, d.selectedOption, d.notes);
  }

  function handleSaveAll() {
    questions.forEach((q) => {
      const d = drafts[q.key];
      if (d) onAnswer(q.key, d.selectedOption, d.notes);
    });
  }

  const answeredCount = allCpQuestionKeys.filter(
    (key) => answers[key]?.selectedOption !== null && answers[key]?.selectedOption !== undefined
  ).length;
  const unansweredCount = allCpQuestionKeys.length - answeredCount;

  const visibleQuestions = useMemo(() => {
    if (tab === "answered") {
      return questions.filter((q) => answers[q.key]?.selectedOption !== null && answers[q.key]?.selectedOption !== undefined);
    }
    if (tab === "unanswered") {
      return questions.filter((q) => !answers[q.key]?.selectedOption);
    }
    return questions;
  }, [tab, questions, answers]);

  // Score display: "—" when zero answered, number otherwise
  const scoreDisplay = cpScore.pct < 0 ? "—" : `${cpScore.pct}`;
  const color = cpScore.pct < 0 ? "#6B6B70" : scoreColor(cpScore.pct);
  const isLastCp = selectedCp === 10;

  // Empty state
  if (!cp || selectedCp === null) {
    return (
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 12,
          padding: "40px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#EFEFEE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <MessageSquare size={24} color="#6B6B70" />
        </div>
        <div
          style={{ fontSize: 15, fontWeight: 700, color: "#0B0B0F", marginBottom: 6 }}
        >
          Select a checkpoint to begin
        </div>
        <div style={{ fontSize: 13, color: "#525258", lineHeight: 1.5 }}>
          Click any orb on the helix or a checkpoint in the legend to open the Q&A workspace.
        </div>
      </div>
    );
  }

  return (
    <div
      key={`qa-open-${openSeq}`}
      style={{
        background: "#FFFFFF",
        borderRadius: 12,
        overflow: "hidden",
        animation: "aipQaSlideIn 320ms cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
    >
      {/* Keyframes for slide-in, header pulse, first-question pulse */}
      <style>{`
        @keyframes aipQaSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes aipQaHeaderPulse {
          0%   { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
          30%  { box-shadow: 0 0 0 6px rgba(220,38,38,0.55); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }
        @keyframes aipQaCardPulse {
          0%   { transform: scale(1);     outline: 0px solid rgba(220,38,38,0); }
          50%  { transform: scale(1.015); outline: 2px solid rgba(220,38,38,0.85); }
          100% { transform: scale(1);     outline: 0px solid rgba(220,38,38,0); }
        }
      `}</style>

      {/* ── Read-only badge ────────────────────────────────────────────── */}
      {readOnly && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "12px 20px 0",
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(234,179,8,0.12)",
            border: "1px solid #EAB308",
            fontSize: 12,
            fontWeight: 600,
            color: "#0B0B0F",
            lineHeight: 1.4,
          }}
          role="status"
          aria-label="Read-only view"
        >
          Read-only view — switch to your own account to answer questions
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "16px 20px",
          borderBottom: "1px solid #E5E5E7",
          background: "#FAFAF7",
          animation: "aipQaHeaderPulse 750ms ease-out 0ms 2 both",
        }}
      >
        {/* CP badge */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#FFFFFF",
            border: `2px solid ${color}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 800,
            color,
            flexShrink: 0,
          }}
        >
          {cp.number}
        </div>

        {/* Title + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#0B0B0F", lineHeight: 1.25, letterSpacing: "-0.01em" }}>
            {cp.fullName}
          </div>
          <div style={{ fontSize: 13, color: "#525258", marginTop: 3, fontWeight: 500 }}>
            {cp.description}
          </div>
          {/* High-contrast count pills */}
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 9px",
                borderRadius: 999,
                background: "#DC2626",
                color: "#FFFFFF",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              Unanswered: {unansweredCount}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 9px",
                borderRadius: 999,
                background: "#16A34A",
                color: "#FFFFFF",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              Answered: {answeredCount}
            </span>
          </div>
        </div>

        {/* Score */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {scoreDisplay}
            {cpScore.pct >= 0 && (
              <span style={{ fontSize: 12, fontWeight: 500, color: "#6B6B70" }}>/100</span>
            )}
          </div>
          <div style={{ fontSize: 10, color: "#6B6B70", marginTop: 2, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Score</div>
        </div>

        {/* Chime toggle */}
        <button
          type="button"
          onClick={() => {
            const next = !chimeOn;
            setChimeOn(next);
            setChimeEnabled(next);
          }}
          aria-label={chimeOn ? "Disable open chime" : "Enable open chime"}
          title={chimeOn ? "Chime: on" : "Chime: off"}
          style={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: 6,
            border: "1px solid #C9C9CC",
            background: "#FFFFFF",
            color: chimeOn ? "#DC2626" : "#6B6B70",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {chimeOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
        </button>

        {/* Close (X) */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "1px solid #C9C9CC",
              background: "#FFFFFF",
              color: "#0B0B0F",
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* ── Tab strip ──────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #E5E5E7",
          background: "#FFFFFF",
        }}
      >
        {(
          [
            { id: "unanswered" as PanelTab, label: `Unanswered (${unansweredCount})`, icon: <MessageSquare size={13} /> },
            { id: "answered"   as PanelTab, label: `Answered (${answeredCount})`,    icon: <MessageSquare size={13} /> },
            { id: "coaching"   as PanelTab, label: "Coaching",                       icon: <BookOpen size={13} /> },
          ] as const
        ).map(({ id, label, icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "12px 16px",
                border: "none",
                borderBottom: active ? "3px solid #DC2626" : "3px solid transparent",
                background: "transparent",
                color: active ? "#DC2626" : "#525258",
                fontSize: 14,
                fontWeight: active ? 700 : 600,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {icon}
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div style={{ padding: "16px 18px" }}>
        {tab === "unanswered" || tab === "answered" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {selectedCp === 9 && q93SubQuestions.length > 0 && (
              <Q93SubQuestions
                questions={q93SubQuestions}
                selectedByKey={q93SelectedByKey}
                onSelect={(qKey, optKey) => {
                  setDrafts((prev) => ({
                    ...prev,
                    [qKey]: {
                      ...(prev[qKey] ?? { selectedOption: null, notes: "" }),
                      selectedOption: optKey,
                    },
                  }));
                  onAnswer(qKey, optKey, drafts[qKey]?.notes ?? "");
                }}
              />
            )}
            {visibleQuestions.length === 0 ? (
              <div style={{ fontSize: 14, color: "#525258", fontWeight: 500, padding: "16px 0" }}>
                {tab === "answered"
                  ? "No answered questions yet for this checkpoint."
                  : selectedCp === 9 && q93SubQuestions.length > 0
                    ? "All graded questions answered. See Paper Process card above."
                    : "All questions answered for this checkpoint."}
              </div>
            ) : (
              visibleQuestions.map((q, idx) => {
                const draft = drafts[q.key] ?? { selectedOption: null, notes: "" };
                const saved = answers[q.key];
                const isDirty =
                  draft.selectedOption !== (saved?.selectedOption ?? null) ||
                  draft.notes !== (saved?.notes ?? "");

                const isActiveCard = idx === 0;
                return (
                  <div
                    key={q.key}
                    style={{
                      background: "#FFFFFF",
                      borderRadius: 10,
                      border: isActiveCard ? "1px solid rgba(220,38,38,0.35)" : "1px solid #E5E5E7",
                      borderLeft: isActiveCard ? "4px solid #DC2626" : "1px solid #E5E5E7",
                      padding: "14px 16px",
                      animation: isActiveCard
                        ? "aipQaCardPulse 600ms ease-out 220ms 1 both"
                        : undefined,
                      transformOrigin: "center",
                      boxShadow: isActiveCard ? "0 1px 3px rgba(15,23,42,0.06)" : "none",
                    }}
                  >
                    {isActiveCard && (
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#DC2626",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          marginBottom: 8,
                        }}
                      >
                        Active question
                      </div>
                    )}
                    {/* Question label */}
                    <div
                      style={{
                        fontSize: 19,
                        fontWeight: 700,
                        color: "#0B0B0F",
                        marginBottom: 16,
                        lineHeight: 1.4,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {/* TICKET-4: I Know / I Think badge next to question number */}
                      <span style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span
                          style={{
                            color: "#6B6B70",
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                          }}
                        >
                          Q{idx + 1}
                        </span>
                        <IKnowBadge
                          state={answers[q.key]?.evidenceType ? "i_know" : "i_think"}
                          evidenceType={answers[q.key]?.evidenceType ?? null}
                          size="sm"
                        />
                      </span>
                      {q.label}
                      {q.helpText && (
                        <div
                          style={{
                            fontSize: 14,
                            color: "#525258",
                            fontWeight: 500,
                            marginTop: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          {q.helpText}
                        </div>
                      )}
                    </div>

                    {/* Per-question coaching panel (DB-sourced) */}
                    <QuestionCoachingPanel
                      coaching={coachingMap[q.key] ?? null}
                      helpText={q.helpText ?? null}
                    />

                    {/* ── 5-point graded options ── */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {GRADED_OPTIONS.map((opt) => {
                        const isSel = draft.selectedOption === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={readOnly ? undefined : () => handleOptionSelect(q.key, opt.value)}
                            disabled={readOnly}
                            style={{
                              textAlign: "left",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              padding: "11px 14px 11px 16px",
                              borderRadius: 8,
                              border: isSel ? "2px solid #DC2626" : "1px solid #E5E5E7",
                              borderLeft: `4px solid ${opt.borderColor}`,
                              background: isSel ? "#FEF2F2" : "#FFFFFF",
                              color: "#0B0B0F",
                              fontSize: 15,
                              lineHeight: 1.45,
                              cursor: readOnly ? "not-allowed" : "pointer",
                              opacity: readOnly ? 0.5 : 1,
                              transition: "background 0.1s, border-color 0.1s",
                              width: "100%",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSel && !readOnly) (e.currentTarget as HTMLButtonElement).style.background = "#FAFAF7";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSel && !readOnly) (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF";
                            }}
                          >
                            <span style={{ fontWeight: isSel ? 700 : 500 }}>
                              {opt.label}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#6B6B70", whiteSpace: "nowrap", flexShrink: 0 }}>
                              {opt.points} {opt.points === 1 ? "pt" : "pts"}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* ── Skip link (hidden in read-only mode) ── */}
                    {!readOnly && (
                      <div style={{ marginTop: 10, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => handleSkip(q.key)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#8E8E94",
                            fontSize: 12,
                            cursor: "pointer",
                            textDecoration: "underline",
                            padding: "4px 8px",
                          }}
                        >
                          Skip — unknown for now
                        </button>
                      </div>
                    )}

                    {/* Notes */}
                    <textarea
                      value={draft.notes}
                      onChange={readOnly ? undefined : (e) => handleNotesChange(q.key, e.target.value)}
                      onBlur={readOnly ? undefined : () => {
                        handleNotesSave(q.key);
                        handleNotesCrossCpCheck(q.key, draft.notes);
                      }}
                      readOnly={readOnly}
                      placeholder="Add context or notes..."
                      rows={2}
                      style={{
                        marginTop: 12,
                        width: "100%",
                        background: readOnly ? "#F5F5F4" : "#FFFFFF",
                        border: "1px solid #C9C9CC",
                        borderRadius: 6,
                        color: "#0B0B0F",
                        fontSize: 14,
                        padding: "8px 11px",
                        resize: readOnly ? "none" : "vertical",
                        outline: "none",
                        fontFamily: "inherit",
                        lineHeight: 1.5,
                        boxSizing: "border-box",
                        cursor: readOnly ? "not-allowed" : undefined,
                        opacity: readOnly ? 0.6 : 1,
                      }}
                      onFocus={readOnly ? undefined : (e) => {
                        e.currentTarget.style.borderColor = "#DC2626";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(220,38,38,0.15)";
                      }}
                      onBlurCapture={readOnly ? undefined : (e) => {
                        e.currentTarget.style.borderColor = "#C9C9CC";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />

                    {/* M-009: Cross-CP out-of-order capture prompts */}
                    {(crossCpMatches[q.key] ?? []).length > 0 && (
                      <div
                        style={{
                          marginTop: 10,
                          borderRadius: 8,
                          border: "1px solid #EA580C",
                          background: "rgba(234,88,12,0.06)",
                          padding: "10px 12px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                        role="region"
                        aria-label="Out-of-order capture prompts"
                      >
                        {(crossCpMatches[q.key] ?? []).map((match) => (
                          <div
                            key={match.cp}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#1F1F23", fontWeight: 500, flex: 1, minWidth: 0 }}>
                              You mentioned{" "}
                              <strong style={{ fontWeight: 700 }}>{match.keyword}</strong>
                              {" "}— want to log that against CP{match.cp} ({match.cpLabel}) too?
                            </span>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button
                                type="button"
                                onClick={() => handleCrossCpLog(q.key, match)}
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #EA580C",
                                  background: "#EA580C",
                                  color: "#FFFFFF",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Log against CP{match.cp}
                              </button>
                              <button
                                type="button"
                                onClick={() => dismissCrossCpMatch(q.key, match.cp)}
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #C9C9CC",
                                  background: "transparent",
                                  color: "#6B6B70",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* TICKET-4: EvidenceSlot — renders below notes, above save row */}
                    {accountId && (
                      <EvidenceSlot
                        accountId={parseInt(accountId, 10)}
                        questionKey={q.key}
                        selectedOption={draft.selectedOption}
                        current={{
                          evidenceType: answers[q.key]?.evidenceType ?? null,
                          evidence: answers[q.key]?.notes ?? undefined,
                          evidenceUrl: answers[q.key]?.evidenceUrl ?? null,
                          evidenceFileName: answers[q.key]?.evidenceFileName ?? null,
                        }}
                        authToken={authToken}
                        readOnly={readOnly}
                        onSaved={(state) => {
                          onEvidenceSaved?.(q.key, state.evidenceType, state.isAssumption);
                        }}
                      />
                    )}

                    {/* Save row (hidden in read-only mode; metadata still visible) */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 10,
                      }}
                    >
                      {saved?.updatedAt ? (
                        <span style={{ fontSize: 11, color: "#6B6B70", fontWeight: 500 }}>
                          {new Date(saved.updatedAt).toLocaleString()} · {saved.updatedBy}
                        </span>
                      ) : (
                        <span />
                      )}
                      {!readOnly && (
                        <button
                          onClick={() => handleNotesSave(q.key)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "6px 14px",
                            borderRadius: 6,
                            border: isDirty ? "none" : "1px solid #C9C9CC",
                            background: isDirty ? "#DC2626" : "#FFFFFF",
                            color: isDirty ? "#FFFFFF" : "#6B6B70",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            transition: "all 0.12s",
                          }}
                        >
                          <Save size={12} />
                          {isDirty ? "Save" : "Saved"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer actions */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: readOnly ? "flex-end" : "space-between",
                paddingTop: 12,
                borderTop: "1px solid #E5E5E7",
                marginTop: 4,
              }}
            >
              {/* Save All — hidden in read-only mode */}
              {!readOnly && (
                <button
                  onClick={handleSaveAll}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 18px",
                    borderRadius: 7,
                    border: "none",
                    background: "#DC2626",
                    color: "#FFFFFF",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <Save size={13} />
                  Save All
                </button>
              )}

              {!isLastCp ? (
                <button
                  onClick={onNextCp}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 18px",
                    borderRadius: 7,
                    border: "1px solid #0B0B0F",
                    background: "#FFFFFF",
                    color: "#0B0B0F",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Next CP
                  <ArrowRight size={13} />
                </button>
              ) : (
                <div style={{ fontSize: 12, color: "#525258", fontWeight: 600 }}>
                  All checkpoints reviewed
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── Coaching tab ──────────────────────────────────────────── */
          <CoachingContent cpNumber={selectedCp} accountId={accountId} />
        )}
      </div>

      {/* M-009: Cross-CP toast */}
      {crossCpToast && (
        <div
          style={{
            position: "sticky",
            bottom: 16,
            left: 0,
            right: 0,
            margin: "8px 16px 0",
            padding: "10px 14px",
            borderRadius: 8,
            background: "#1C1C21",
            color: "#FAFAF7",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            zIndex: 20,
          }}
          role="status"
        >
          {crossCpToast}
        </div>
      )}
    </div>
  );
}

// ─── Per-question coaching panel (DB-sourced) ──────────────────────────────

function QuestionCoachingPanel({
  coaching,
}: {
  coaching: QuestionCoaching | null;
  helpText: string | null;
}) {
  const [expanded, setExpanded] = useState(false);

  // Render nothing if no coaching row exists for this question.
  if (!coaching) return null;

  // Extract the first sentence of why_it_matters for the collapsed preview.
  const whyPreview = coaching.why_it_matters
    ? (coaching.why_it_matters.split(/\.\s+/)[0] ?? "") + "."
    : null;

  return (
    <div
      style={{
        marginBottom: 12,
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        background: "#FAFAF7",
        overflow: "hidden",
      }}
    >
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 12px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#DC2626",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            Coaching
          </span>
          {!expanded && whyPreview && (
            <span
              style={{
                fontSize: 12,
                color: "#525258",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {whyPreview}
            </span>
          )}
          {!expanded && (
            <span
              style={{
                fontSize: 11,
                color: "#8E8E94",
                fontWeight: 500,
                flexShrink: 0,
                marginLeft: "auto",
              }}
            >
              More guidance
            </span>
          )}
        </div>
        <span style={{ color: "#6B6B70", flexShrink: 0 }}>
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: "0 12px 12px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ borderTop: "1px solid #E5E5E7", marginBottom: 2 }} />

          {/* 1. Why it matters */}
          {coaching.why_it_matters && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#DC2626",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: 5,
                }}
              >
                Why it matters
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "#0B0B0F",
                  fontWeight: 500,
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {coaching.why_it_matters}
              </p>
            </div>
          )}

          {/* 2. What good looks like */}
          {coaching.best_practice_answers && coaching.best_practice_answers.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#DC2626",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: 5,
                }}
              >
                What good looks like
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {coaching.best_practice_answers.map((bpa, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 13,
                      color: "#0B0B0F",
                      fontWeight: 500,
                      lineHeight: 1.55,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{bpa.option_label}.</span>{" "}
                    {bpa.guidance}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 3. Recommended approach */}
          {coaching.recommendation && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#DC2626",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: 5,
                }}
              >
                Recommended approach
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "#0B0B0F",
                  fontWeight: 500,
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {coaching.recommendation}
              </p>
            </div>
          )}

          {/* 4. Narrative context */}
          {coaching.narrative && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#6B6B70",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: 5,
                }}
              >
                Narrative context
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "#525258",
                  fontWeight: 500,
                  lineHeight: 1.65,
                  margin: 0,
                }}
              >
                {coaching.narrative}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Coaching content (CP-level, coaching tab) ────────────────────────────────

function CoachingContent({
  cpNumber,
  accountId,
}: {
  cpNumber: number;
  accountId?: string;
}) {
  const coaching = CP_COACHING[cpNumber];
  // D-038: link from coaching block back to the dimension question set for
  // this CP. Mapping is the canonical CP→dimension table shared with
  // AccountScorecard's Overview cards.
  const dimensionId = dimensionIdForCp(cpNumber);
  // D-046 (2026-05-21): use wouter's programmatic navigation instead of an
  // anchor with a hash href. The hash-anchor approach worked for URL
  // change but did not scroll to top — DimensionFlow's OperatorShell was
  // mounted off-screen below the Helix scroll position, making it look
  // like the page rendered in the legacy light shell. Programmatic
  // navigation + scrollTo(0,0) guarantees the dark shell is visible.
  const [, setLocation] = useLocation();

  if (!coaching) {
    return (
      <div style={{ color: "#525258", fontSize: 14, fontWeight: 500, padding: "12px 0" }}>
        No coaching content for this checkpoint.
      </div>
    );
  }

  const sections: Array<{ label: string; field: keyof typeof coaching }> = [
    { label: "Purpose", field: "purpose" },
    { label: "In Conversation", field: "inConversation" },
    { label: "On the Fence", field: "onTheFence" },
    { label: "Overall Impact", field: "overallImpact" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Stage badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderRadius: 8,
          background: "#FAFAF7",
          border: "1px solid #E5E5E7",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#6B6B70",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          Stage
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>
          {coaching.stage}
        </span>

      </div>

      {sections.map(({ label, field }) => (
        <div key={field}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#DC2626",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              marginBottom: 7,
            }}
          >
            {label}
          </div>
          <p
            style={{
              fontSize: 14,
              color: "#0B0B0F",
              fontWeight: 500,
              lineHeight: 1.65,
              margin: 0,
            }}
          >
            {coaching[field] as string}
          </p>
        </div>
      ))}

      {/* D-038: link out to the dimension question set for this CP. Discreet
          underline-on-hover affordance below the coaching content. Renders
          only when an accountId is in scope and a dimension exists for the
          CP (10/10 do today). */}
      {accountId && dimensionId && (
        <div
          style={{
            borderTop: "1px solid #E5E5E7",
            paddingTop: 14,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            data-testid="open-question-set-link"
            onClick={() => {
              setLocation(`/account/${accountId}/dimension/${dimensionId}`);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 13,
              fontWeight: 600,
              color: "#0B0B0F",
              background: "transparent",
              border: "none",
              borderBottom: "1px solid transparent",
              padding: 0,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderBottomColor = "#0B0B0F";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderBottomColor = "transparent";
            }}
          >
            Open question set
            <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
          </button>
        </div>
      )}
    </div>
  );
}
